/**
 * Small presentational primitives.
 *
 * Deliberately plain: the portal is a handful of forms, so a local set of
 * styled elements is easier to reason about than a component library. Every
 * control keeps its native semantics so browser and assistive-technology
 * behaviour is preserved.
 */
import { useId, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
} from 'react';
import { cn } from '@/lib/format';

/* ----------------------------- Button ----------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'onDark';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 disabled:hover:bg-brand-600',
  secondary: 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  // Outlined control for the dark auth panel (Google sign-in and similar).
  onDark:
    'bg-white/[0.04] text-slate-100 ring-1 ring-inset ring-white/15 hover:bg-white/[0.09] disabled:hover:bg-white/[0.04]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shows a spinner and blocks input without changing the button's width. */
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      // Communicates the pending state to screen readers, which cannot see the
      // spinner.
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5',
        'text-sm font-medium transition-colors',
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}

/* ----------------------------- Spinner ---------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  );
}

/** Full-area loading state, used while the session probe is in flight. */
export function LoadingScreen({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-svh items-center justify-center" role="status">
      <Spinner className="size-8 text-brand-600" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/* ------------------------------ Label ----------------------------- */

export function Label({ className, children, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={cn('block text-sm font-medium text-slate-700', className)} {...rest}>
      {children}
    </label>
  );
}

/* ------------------------------ Input ----------------------------- */

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';
  return (
    <input
      className={cn(
        'block w-full rounded-lg border-0 px-3 py-2.5 text-slate-900 shadow-sm',
        'ring-1 ring-inset placeholder:text-slate-400',
        'focus:ring-2 focus:ring-inset',
        invalid
          ? 'ring-red-400 focus:ring-red-500'
          : 'ring-slate-300 focus:ring-brand-600',
        'disabled:bg-slate-50 disabled:text-slate-500',
        className,
      )}
      {...rest}
    />
  );
}

/* ------------------------------ Field ----------------------------- */

export interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactNode;
}

/**
 * Label + control + error, wired together. The error carries `role="alert"` so
 * it is announced when it appears, and its id matches the control's
 * `aria-describedby` from `useZodForm().fieldProps`.
 */
export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      ) : (
        hint && <p className="text-sm text-slate-500">{hint}</p>
      )}
    </div>
  );
}

/* ------------------------------ Alert ----------------------------- */

type AlertTone = 'error' | 'success' | 'info' | 'warning';

const ALERT_TONES: Record<AlertTone, string> = {
  error: 'bg-red-50 text-red-800 ring-red-200',
  success: 'bg-green-50 text-green-800 ring-green-200',
  info: 'bg-brand-50 text-brand-900 ring-brand-200',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200',
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      // Errors interrupt; everything else is announced politely.
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('rounded-lg px-4 py-3 text-sm ring-1 ring-inset', ALERT_TONES[tone], className)}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={cn(title && 'mt-1')}>{children}</div>}
    </div>
  );
}

/* ------------------------------ Card ------------------------------ */

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200', className)}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-slate-600">{description}</p>}
    </div>
  );
}

/* ------------------- Dark-panel (auth screen) set ------------------ */

/**
 * The auth screens sit on a dark split panel, so they need their own surface
 * treatment. These are separate components rather than a `tone` prop on the
 * light set: the two are used in disjoint places (auth shell vs. account area)
 * and keeping them apart avoids threading a tone through every call site.
 */

/** Small leading glyphs for the auth inputs. */
export const FieldIcons = {
  mail: (
    <>
      <rect x="2.6" y="4.4" width="14.8" height="11.2" rx="2.2" />
      <path d="m3.6 6 6.4 4.6L16.4 6" />
    </>
  ),
  lock: (
    <>
      <rect x="3.6" y="8.6" width="12.8" height="8.4" rx="2" />
      <path d="M6.6 8.6V6.4a3.4 3.4 0 0 1 6.8 0v2.2" />
    </>
  ),
} as const;

function FieldIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className="pointer-events-none absolute left-3.5 top-1/2 size-[1.15rem] -translate-y-1/2 text-slate-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export interface DarkInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode;
}

/**
 * Text input for the dark panel. When `type="password"`, a reveal toggle is
 * added; it flips the input's own type so the browser's password manager still
 * recognises the field.
 */
export function DarkInput({ className, icon, type = 'text', ...rest }: DarkInputProps) {
  const [revealed, setRevealed] = useState(false);
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';
  const isPassword = type === 'password';

  return (
    <div className="relative">
      {icon && <FieldIcon>{icon}</FieldIcon>}
      <input
        type={isPassword && revealed ? 'text' : type}
        className={cn(
          'block w-full rounded-lg border-0 bg-white/[0.04] py-2.5 text-sm text-white',
          'ring-1 ring-inset placeholder:text-slate-500',
          'focus:bg-white/[0.06] focus:ring-2 focus:ring-inset',
          icon ? 'pl-10' : 'pl-3.5',
          isPassword ? 'pr-11' : 'pr-3.5',
          invalid
            ? 'ring-red-500/60 focus:ring-red-500'
            : 'ring-white/12 focus:ring-brand-500',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...rest}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setRevealed((current) => !current)}
          // The label is the action, not the state, so it reads correctly when
          // announced on focus.
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer rounded p-1.5 text-slate-500 transition-colors hover:text-slate-300"
        >
          <svg
            viewBox="0 0 20 20"
            className="size-[1.15rem]"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1.8 10S4.9 4.4 10 4.4 18.2 10 18.2 10 15.1 15.6 10 15.6 1.8 10 1.8 10Z" />
            <circle cx="10" cy="10" r="2.6" />
            {revealed && <path d="M3.4 3.4l13.2 13.2" />}
          </svg>
        </button>
      )}
    </div>
  );
}

/** Label + control + error for the dark panel. */
export function DarkField({
  label,
  htmlFor,
  error,
  hint,
  action,
  children,
}: FieldProps & { /** Rendered opposite the label, e.g. a "Forgot password?" link. */ action?: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-sm font-medium text-slate-300">
          {label}
        </label>
        {action}
      </div>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-sm text-red-400">
          {error}
        </p>
      ) : (
        hint && <p className="text-sm text-slate-500">{hint}</p>
      )}
    </div>
  );
}

/** Error/status banner for the dark panel. */
const DARK_ALERT_TONES: Record<AlertTone, string> = {
  error: 'bg-red-500/10 text-red-200 ring-red-500/30',
  success: 'bg-green-500/10 text-green-200 ring-green-500/30',
  info: 'bg-brand-500/10 text-brand-100 ring-brand-500/30',
  warning: 'bg-amber-500/10 text-amber-100 ring-amber-500/30',
};

export function DarkAlert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'rounded-lg px-4 py-3 text-sm ring-1 ring-inset',
        DARK_ALERT_TONES[tone],
        className,
      )}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={cn(title && 'mt-1')}>{children}</div>}
    </div>
  );
}

/**
 * "Continue to Meridian" banner shown when the user arrived from a module.
 *
 * The name comes from the server's application registry, never from the URL, so
 * a crafted link cannot make this page claim to be a module it is not.
 * Rendering nothing when the name is unknown is deliberate: a generic
 * "continue to the application" adds no information and invites the user to
 * trust an unverified destination.
 */
export function ModuleBanner({ applicationName }: { applicationName: string | null }) {
  if (!applicationName) return null;

  return (
    <div
      role="status"
      className="mb-5 flex items-center gap-3 rounded-lg bg-brand-500/10 px-4 py-3 text-sm ring-1 ring-inset ring-brand-500/30"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="size-4 shrink-0 text-brand-300"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
      <span className="text-brand-100">
        Continue to <span className="font-semibold">{applicationName}</span>
      </span>
    </div>
  );
}

/** Rule with centred caption, e.g. "OR" between sign-in methods. */
export function DarkDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4" role="separator">
      <span className="h-px flex-1 bg-white/10" />
      <span className="text-[0.7rem] font-medium uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

/** Google's mark, in its brand colours. */
export function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 18 18" className={cn('size-[1.1rem]', className)} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.94v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.94a9 9 0 0 0 0 8.1l3.03-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .94 4.95l3.03 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * Primary submit for the dark panel — the gradient call-to-action.
 *
 * `lock` adds the padlock glyph used on the sign-in action to signal a secured
 * submission; screens whose action is not a credential submission omit it.
 */
export function DarkSubmit({
  loading = false,
  lock = false,
  className,
  children,
  disabled,
  type = 'submit',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean; lock?: boolean }) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3',
        'bg-linear-to-r from-brand-600 to-brand-500 text-sm font-semibold text-white',
        'shadow-lg shadow-brand-900/40 transition-[filter,opacity] hover:brightness-110',
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:brightness-100',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner className="size-4" />
      ) : (
        lock && (
          <svg
            viewBox="0 0 20 20"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {FieldIcons.lock}
          </svg>
        )
      )}
      {children}
    </button>
  );
}

/**
 * Secondary and destructive actions for the dark panel, styled as outlines so
 * the gradient `DarkSubmit` stays the single obvious primary action per screen.
 */
export function DarkButton({
  tone = 'secondary',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'secondary' | 'danger';
  loading?: boolean;
}) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3',
        'text-sm font-semibold ring-1 ring-inset transition-colors',
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-60',
        tone === 'danger'
          ? 'bg-red-500/10 text-red-200 ring-red-500/40 hover:bg-red-500/20'
          : 'bg-white/[0.04] text-slate-100 ring-white/15 hover:bg-white/[0.09]',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}

/** The reassurance line under the auth card. */
export function SecurityNote({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-center justify-center gap-2 text-xs text-slate-500">
      <svg
        viewBox="0 0 20 20"
        className="size-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M10 2.2 16.3 4.6v4.8c0 3.6-2.5 6.9-6.3 8.4-3.8-1.5-6.3-4.8-6.3-8.4V4.6z" />
      </svg>
      {children}
    </p>
  );
}

/** Checkbox for the dark panel. */
export function DarkCheckbox({
  label,
  error,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; error?: string | undefined }) {
  const generated = useId();
  const id = rest.id ?? generated;
  return (
    <div>
      <div className="flex items-start gap-2.5">
        <input
          id={id}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'mt-0.5 size-4 cursor-pointer rounded border-white/20 bg-white/[0.06] text-brand-500',
            'focus:ring-brand-500 focus:ring-offset-0',
            className,
          )}
          {...rest}
        />
        <label htmlFor={id} className="cursor-pointer text-sm text-slate-400">
          {label}
        </label>
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

/* ---------------------------- Checkbox ---------------------------- */

export function Checkbox({
  label,
  error,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; error?: string | undefined }) {
  return (
    <div>
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          className={cn(
            'mt-0.5 size-4 rounded border-slate-300 text-brand-600',
            'focus:ring-brand-600',
            className,
          )}
          {...rest}
        />
        <label htmlFor={rest.id} className="text-sm text-slate-700">
          {label}
        </label>
      </div>
      {error && (
        <p id={rest.id ? `${rest.id}-error` : undefined} role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
